import { TColumnName, TDataset } from "../types";
import RowUtils from "./RowUtils";
import { mean, round, std } from "mathjs";
const StatisticsJS = require("statistics.js");

const dotMatrix = function (a: number[], b: number[]) {
  let sum = 0;
  let i;
  let v;

  for (i = 0; i < a.length; ++i) {
    v = a[i] * b[i];
    if (v === v) sum += v;
  }
  return sum;
};

class MathUtils {
  static isNumberAsString = (value: any) => {
    if (value === null || value === undefined) {
      return false;
    }

    return !isNaN(Number(value));
  };

  // https://github.com/vega/datalib/blob/707ec72a9dbc53b20871a9bef9634f2c6892ad5d/src/stats.js#L257
  static getPCor(a: number[], b: number[]) {
    const dot = dotMatrix(a, b);
    const mua = mean(a);
    const mub = mean(b);
    const sda = std(...a);
    const sdb = std(...b);
    const n = a.length;

    return (dot - n * mua * mub) / ((n - 1) * sda * sdb);
  }

  static getVariance(X: number[], Y: number[], Z: number[], V: number[]) {
    const RSquare = (pCor: number) => Math.pow(pCor, 2);

    return {
      r_sq_x: RSquare(MathUtils.getPCor(X, V)),
      r_sq_y: RSquare(MathUtils.getPCor(Y, V)),
      r_sq_z: RSquare(MathUtils.getPCor(Z, V)),
    };
  }

  static getRho(X: number[], Y: number[], Z: number[], V: number[]) {
    const data = X.map((x, i) => ({
      X: x,
      Y: Y[i],
      Z: Z[i],
      V: V[i],
    }));

    const stats = new StatisticsJS(data, {
      X: "interval",
      Y: "interval",
      Z: "interval",
      V: "interval",
    });

    return {
      rho_x: stats.spearmansRho("X", "V").rho,
      rho_y: stats.spearmansRho("Y", "V").rho,
      rho_z: stats.spearmansRho("Z", "V").rho,
    };
  }

  static findStandardDeviations = (
    rows: TDataset,
    numericalColumns: TColumnName[]
  ) => {
    const numericalColumnValues: Record<string, number[]> = {};
    const standardDeviations: Record<string, number> = {};
    const means: Record<string, number> = {};
    const smallestValue: Record<string, number> = {};
    const largestValue: Record<string, number> = {};

    numericalColumns.forEach((column) => {
      numericalColumnValues[column] = [];
      smallestValue[column] = Infinity;
      largestValue[column] = 0;
    });

    const rowsSize = rows.length;
    for (let i = 0; i < rowsSize; i++) {
      numericalColumns.forEach((column) => {
        const value = RowUtils.getValue(rows[i], column);
        if (MathUtils.isNumberAsString(value)) {
          const valueAsNumber = Number(value);
          numericalColumnValues[column].push(valueAsNumber);

          if (valueAsNumber < smallestValue[column]) {
            smallestValue[column] = valueAsNumber;
          }

          if (valueAsNumber > largestValue[column]) {
            largestValue[column] = valueAsNumber;
          }
        }
      });
    }

    Object.keys(numericalColumnValues).forEach((column) => {
      means[column] = mean(numericalColumnValues[column]);
      standardDeviations[column] = std(...numericalColumnValues[column]);
    });

    return { standardDeviations, means, smallestValue, largestValue };
  };

  static round(value: number, decimals: number = 4) {
    return round(value, decimals);
  }

  static percentage(value: number, decimals: number = 2) {
    return MathUtils.round(value * 100, decimals);
  }
}

export default MathUtils;
